import { Injectable } from '@angular/core';
import CustomDnsBinding, { CustomDnsBindingQueryParams } from 'mbs-ui-shared@models/CustomDnsBinding';
import CustomDnsCertificate from 'mbs-ui-shared@models/CustomDnsCertificate';
import { Store } from '@ngrx/store';
import { CustomDnsBindingsService } from 'mbs-ui-shared@services/custom-dns-bindings.service';
import { CustomDnsCertificatesService } from 'mbs-ui-shared@services/custom-dns-certificates.service';
import { CustomDnsBindingStore } from '../store/customDnsBinding/customDnsBindingStore';
import { CustomDnsBindingStoreProperties } from '../store/customDnsBinding/customDnsBindingStore.types';
import { PagingStoreFacade } from '../store/pagingStoreFactory/pagingStore.facade';
import { Observable, concatMap, filter, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { PagedResponse } from 'mbs-ui-shared@models';

@Injectable({
  providedIn: 'root'
})
export class CustomDnsBindingFacade extends PagingStoreFacade<CustomDnsBinding, CustomDnsBindingStoreProperties> {
  constructor(
    public store: Store,
    public customDnsBindingStore: CustomDnsBindingStore,
    private customDnsBindingService: CustomDnsBindingsService,
    private certificatesService: CustomDnsCertificatesService
  ) {
    super(store, customDnsBindingStore);
  }

  // specific
  getCertificates$ = this.store.select(this.customDnsBindingStore.selectors.selectCertificates);
  getCertificatesLoaded$ = this.store.select(this.customDnsBindingStore.selectors.selectLoadedCertificates);
  getCertificatesLoading$ = this.store.select(this.customDnsBindingStore.selectors.selectLoadingCertificates);
  getCertificatesWhenLoaded$ = this.getCertificates$.pipe(
    withLatestFrom(this.getCertificatesLoaded$),
    filter(([, loaded]) => loaded),
    map(([data]) => data)
  );

  // service
  getBindings(params?: CustomDnsBindingQueryParams): Observable<PagedResponse<CustomDnsBinding>> {
    return this.customDnsBindingService.get(params);
  }

  create(entity: CustomDnsBinding): Observable<CustomDnsBinding> {
    return this.customDnsBindingService.create(entity).pipe(tap(() => this.invalidateEntities()));
  }

  update(entity: CustomDnsBinding): Observable<CustomDnsBinding> {
    return this.customDnsBindingService.update(entity).pipe(tap((entity) => this.invalidateEntities(entity)));
  }

  deleteRelatedBindingsInCurrentPage(entity: CustomDnsBinding): Observable<null> {
    return this.getCurrentPage$.pipe(
      take(1),
      map((bindings) =>
        entity.httpBindingId
          ? `${entity.httpBindingId}`
          : bindings.find((b) => b.dnsName === entity.dnsName && b.certificate === 0)?.id ?? ''
      ),
      switchMap((httpBindingId) => {
        return httpBindingId ? this.deleteBinding(httpBindingId) : of(true);
      }),
      concatMap(() => this.deleteBinding(entity.id)),
      tap(() => this.invalidateEntities())
    );
  }

  private deleteBinding(id: string): Observable<null> {
    return this.customDnsBindingService.deleteBinding(id);
  }

  loadCertificates(force?: boolean): void {
    this.store.dispatch(this.customDnsBindingStore.actions.loadCertificates({ force }));
  }

  uploadCertificate({ certificate, password }): Observable<CustomDnsCertificate> {
    return this.certificatesService.uploadCertificate({ certificate, password }).pipe(tap(() => this.loadCertificates(true)));
  }

  private invalidateEntities(entity?: CustomDnsBinding): void {
    this.refreshAfterCRUD(entity);
  }
}
